import { useCallback, useRef, useState } from "react";
import { socketService } from "../services/socketService";

// PCM to WAV conversion utility
const pcmToWav = (pcmData: Uint8Array, sampleRate: number, channels: number): ArrayBuffer => {
  const length = pcmData.length;
  const buffer = new ArrayBuffer(44 + length);
  const view = new DataView(buffer);

  // WAV header
  const writeString = (offset: number, string: string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };

  writeString(0, "RIFF");
  view.setUint32(4, 36 + length, true);
  writeString(8, "WAVE");
  writeString(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, channels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * channels * 2, true);
  view.setUint16(32, channels * 2, true);
  view.setUint16(34, 16, true);
  writeString(36, "data");
  view.setUint32(40, length, true);

  // Copy PCM data
  const wavData = new Uint8Array(buffer, 44);
  wavData.set(pcmData);

  return buffer;
};

export interface AudioState {
  audioEnabled: boolean;
  isAudioPlaying: boolean;
  userHasInteracted: boolean;
}

export interface AudioMessage {
  type: "ai" | "user" | "status";
  content?: string;
  is_html?: boolean;
  id: string;
}

export const useAudioProcessor = () => {
  // Audio state
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [isAudioLoading, setIsAudioLoading] = useState(false);
  const [userHasInteracted, setUserHasInteracted] = useState(false);

  // Audio refs
  const audioRef = useRef<HTMLAudioElement>(null);
  const audioQueueRef = useRef<string[]>([]);
  const utterBuffersRef = useRef<
    Map<string, { chunks: Uint8Array[]; format: string; sample_rate: number }>
  >(new Map());

  // Audio handling functions - Order matters for dependencies
  const playNextInQueueRef = useRef<() => void>();

  const playNextInQueue = useCallback(() => {
    const playNext = async () => {
      if (audioQueueRef.current.length === 0) {
        setIsAudioPlaying(false);
        return;
      }

      const audioUrl = audioQueueRef.current.shift()!;

      if (!audioRef.current) {
        console.error("Audio element not found");
        setIsAudioPlaying(false);
        return;
      }

      audioRef.current.src = audioUrl;

      try {
        await audioRef.current.play();
      } catch (error) {
        console.error("Error playing audio:", error);
        // Continue to next item in queue
        setTimeout(() => playNextInQueueRef.current?.(), 100);
      }
    };

    playNext();
  }, []);

  // Store the function in ref for recursive calls
  playNextInQueueRef.current = playNextInQueue;

  const playAudioBuffer = useCallback(
    async (buffer: Uint8Array, format: string, sampleRate: number, utterId: string) => {
      try {
        let audioBlob: Blob;

        if (format === "pcm") {
          // Convert PCM to WAV
          const wavBuffer = pcmToWav(buffer, sampleRate, 1); // 1 channel (mono)
          audioBlob = new Blob([wavBuffer], { type: "audio/wav" });
        } else if (format === "mp3") {
          audioBlob = new Blob([new Uint8Array(buffer)], { type: "audio/mpeg" });
        } else if (format === "opus") {
          // Opus is typically in OGG container for web
          audioBlob = new Blob([new Uint8Array(buffer)], { type: "audio/ogg; codecs=opus" });
        } else if (format === "webm") {
          audioBlob = new Blob([new Uint8Array(buffer)], { type: "audio/webm" });
        } else if (format === "wav") {
          audioBlob = new Blob([new Uint8Array(buffer)], { type: "audio/wav" });
        } else {
          console.error("Unsupported audio format:", format);
          return;
        }

        const audioUrl = URL.createObjectURL(audioBlob);

        // Add to play queue
        audioQueueRef.current.push(audioUrl);

        // If not currently playing, start playback (with user interaction check)
        if (!isAudioPlaying) {
          setIsAudioPlaying(true);
          const nextUrl = audioQueueRef.current.shift()!;

          if (audioRef.current) {
            audioRef.current.src = nextUrl;
            try {
              await audioRef.current.play();
            } catch (error: any) {
              if (error.name === "NotAllowedError") {
                console.warn("Audio playback requires user interaction");
                // Reset playing state since we couldn't start
                setIsAudioPlaying(false);
                // Try to play next in queue if available
                if (audioQueueRef.current.length > 0) {
                  setTimeout(() => playNextInQueueRef.current?.(), 100);
                }
              } else {
                console.error("Error playing audio:", error);
                setIsAudioPlaying(false);
              }
            }
          } else {
            console.error("Audio element not found");
            setIsAudioPlaying(false);
          }
        }
      } catch (error) {
        console.error("Error playing audio buffer:", error);
      }
    },
    [isAudioPlaying]
  );

  const processAudioChunk = useCallback(
    (chunk: any) => {
      if (!audioEnabled) {
        return;
      }

      // Set loading state when processing chunks
      setIsAudioLoading(true);

      // Backend sends audio_chunk messages with nested data structure
      const chunkData = chunk.data || chunk;

      const { utterance_id, chunk_b64, is_last, format = "opus", sample_rate = 24000 } = chunkData;

      if (!utterance_id) {
        console.warn("No utterance_id found in chunk:", chunk);
        setIsAudioLoading(false);
        return;
      }

      // Get or create utter buffer
      if (!utterBuffersRef.current.has(utterance_id)) {
        utterBuffersRef.current.set(utterance_id, { chunks: [], format, sample_rate });
      }

      const utterBuffer = utterBuffersRef.current.get(utterance_id)!;

      if (chunk_b64) {
        try {
          const audioData = Uint8Array.from(atob(chunk_b64), (c) => c.charCodeAt(0));
          utterBuffer.chunks.push(audioData);
        } catch (error) {
          console.error("Failed to decode audio chunk:", error);
          setIsAudioLoading(false);
          return;
        }
      }

      if (is_last) {
        // Combine all chunks for this utterance
        const totalLength = utterBuffer.chunks.reduce((sum, chunk) => sum + chunk.length, 0);
        const combinedBuffer = new Uint8Array(totalLength);
        let offset = 0;

        for (const chunk of utterBuffer.chunks) {
          combinedBuffer.set(chunk, offset);
          offset += chunk.length;
        }

        // Convert to audio and play
        playAudioBuffer(combinedBuffer, utterBuffer.format, utterBuffer.sample_rate, utterance_id);

        // Clean up
        utterBuffersRef.current.delete(utterance_id);

        // Clear loading state when processing is complete
        setIsAudioLoading(false);
      }
    },
    [audioEnabled, playAudioBuffer]
  );

  // Handle user interaction to enable audio playback
  const enableAudioPlayback = useCallback(async () => {
    if (!userHasInteracted) {
      try {
        // Create audio context on user interaction
        const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
        if (ctx.state === "suspended") {
          await ctx.resume();
        }
        setUserHasInteracted(true);

        // Try to play queued audio if any
        if (audioQueueRef.current.length > 0 && !isAudioPlaying) {
          playNextInQueue();
        }
      } catch (error) {
        console.error("Failed to enable audio context:", error);
      }
    }
  }, [userHasInteracted, isAudioPlaying, playNextInQueue]);

  // Function to trigger TTS for the last AI message or all messages if first time
  const triggerTTSForLastMessage = useCallback((messages: AudioMessage[]) => {
    // Check if user has provided any input yet
    const hasUserInput = messages.some((msg) => msg.type === "user");

    if (!hasUserInput) {
      // First time enabling audio with no user input - read all AI messages
      const aiMessages = messages.filter(
        (msg) => msg.type === "ai" && msg.content && msg.content.trim().length > 0
      );

      if (aiMessages.length > 0) {
        // Process each AI message with a small delay between them
        aiMessages.forEach((message, index) => {
          setTimeout(() => {
            // Strip HTML tags if the content has HTML
            const textContent = message.is_html
              ? message.content!.replace(/<[^>]*>/g, "")
              : message.content!;

            // Only send TTS request if there's actual text content
            if (textContent.trim().length > 0) {
              socketService.requestTTS(textContent.trim());
            }
          }, index * 500); // 500ms delay between messages
        });
      }
    } else {
      // User has provided input - only read the most recent AI message
      const lastAIMessage = messages
        .slice()
        .reverse()
        .find((msg) => msg.type === "ai" && msg.content && msg.content.trim().length > 0);

      if (lastAIMessage && lastAIMessage.content) {
        // Strip HTML tags if the content has HTML
        const textContent = lastAIMessage.is_html
          ? lastAIMessage.content.replace(/<[^>]*>/g, "")
          : lastAIMessage.content;

        // Only send TTS request if there's actual text content
        if (textContent.trim().length > 0) {
          // Add a small delay to ensure audio context is ready
          setTimeout(() => {
            socketService.requestTTS(textContent.trim());
          }, 100);
        }
      }
    }
  }, []);

  // Stop current playback without disabling audio system
  const stopCurrentPlayback = useCallback(() => {
    // Stop current audio playback
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;

      // Clean up current audio URL
      if (audioRef.current.src) {
        URL.revokeObjectURL(audioRef.current.src);
        audioRef.current.src = "";
      }
    }

    // Clear the audio queue and clean up URLs
    audioQueueRef.current.forEach((url) => {
      URL.revokeObjectURL(url);
    });
    audioQueueRef.current = [];

    // Reset playing and loading states
    setIsAudioPlaying(false);
    setIsAudioLoading(false);

    // Clear any incomplete utterance buffers
    utterBuffersRef.current.clear();
  }, []);

  // Handle audio enabled state changes
  const handleAudioEnabledChange = useCallback(
    (enabled: boolean, messages: AudioMessage[]) => {
      setAudioEnabled(enabled);

      // If audio is being enabled, trigger TTS for the last message
      if (enabled && !audioEnabled) {
        // Small delay to ensure audio context is set up
        setTimeout(() => {
          triggerTTSForLastMessage(messages);
        }, 200);
      }

      // If audio is being disabled, stop any currently playing audio
      if (!enabled && audioEnabled) {
        stopCurrentPlayback();
      }
    },
    [audioEnabled, triggerTTSForLastMessage, stopCurrentPlayback]
  );

  // Handle audio ended event
  const handleAudioEnded = useCallback(() => {
    // Remove the URL object to free memory
    if (audioRef.current?.src) {
      URL.revokeObjectURL(audioRef.current.src);
    }
    // Play next audio in queue
    if (audioQueueRef.current.length > 0) {
      playNextInQueue();
    } else {
      setIsAudioPlaying(false);
    }
  }, [playNextInQueue]);

  // Handle audio error event
  const handleAudioError = useCallback(
    (e: any) => {
      console.error("Audio playback error:", e);
      if (audioQueueRef.current.length > 0) {
        playNextInQueue();
      } else {
        setIsAudioPlaying(false);
      }
    },
    [playNextInQueue]
  );

  return {
    // State
    audioEnabled,
    isAudioPlaying,
    isAudioLoading,
    userHasInteracted,

    // Refs for JSX
    audioRef,

    // Functions
    processAudioChunk,
    enableAudioPlayback,
    stopCurrentPlayback,
    handleAudioEnabledChange,
    triggerTTSForLastMessage,
    handleAudioEnded,
    handleAudioError,
  };
};
